import React from 'react';
import {
	getBeginNext,
	getBeginNext2,
	getBeginPrevious,
	getBeginPrevious2,
	getEndPrevious,
	getEndPrevious2,
} from './shared/dates';
import { formatMonthYear as defaultFormatMonthYear } from './shared/dateFormatter';
import type { NavigationLabelFunc } from './shared/types';
import { getYear, isAfter, isEqual, isBefore, isSameMonth, subDays } from 'date-fns';
import { Action } from '@fshr/shared/types';
import { cn } from '@fshr/shared-react';
import { IconChevronLeft, IconChevronRight } from '../icon';
import { getRTL } from '@micro-apps/env';
import BevSafeText from '../bev-safe-text';

type NavigationProps = {
	activeStartDate: Date;
	formatMonthYear?: typeof defaultFormatMonthYear;
	locale: string;
	maxDate?: Date;
	minDate?: Date;
	navigationAriaLabel?: string | ((activeStartDate: Date, showDoubleView?: boolean) => string);
	navigationAriaLive?: 'off' | 'polite' | 'assertive';
	navigationLabel?: NavigationLabelFunc;
	next2AriaLabel?: string;
	next2Label?: React.ReactNode;
	nextAriaLabel?: string;
	prev2AriaLabel?: string;
	prev2Label?: React.ReactNode;
	prevAriaLabel?: string;
	setActiveStartDate: (nextActiveStartDate: Date, action: Action) => void;
	showDoubleView?: boolean;
};

export default function Navigation({
	activeStartDate,
	formatMonthYear = defaultFormatMonthYear,
	locale,
	maxDate,
	minDate,
	navigationAriaLabel = '',
	navigationAriaLive,
	navigationLabel,
	nextAriaLabel = '',
	prevAriaLabel = '',
	setActiveStartDate,
	showDoubleView,
}: NavigationProps) {
	const previousActiveStartDate = getBeginPrevious(activeStartDate);
	const previousActiveStartDate2 = getBeginPrevious2(activeStartDate);
	const nextActiveStartDate = getBeginNext(activeStartDate);
	const nextActiveStartDate2 = getBeginNext2(activeStartDate);

	const prevButtonDisabled = (() => {
		if (getYear(previousActiveStartDate) < 0) {
			return true;
		}
		const previousActiveEndDate = getEndPrevious(activeStartDate);
		return minDate
			? isAfter(minDate, previousActiveEndDate) || isEqual(minDate, previousActiveEndDate)
			: false;
	})();

	const nextButtonDisabled = maxDate
		? isBefore(maxDate, nextActiveStartDate) || isSameMonth(subDays(maxDate, 1), activeStartDate)
		: false;

	function onClickPrevious() {
		setActiveStartDate(previousActiveStartDate, 'prev');
	}

	function onClickNext() {
		setActiveStartDate(nextActiveStartDate, 'next');
	}

	return (
		<div className="flex items-center w-full relative">
			<NavigationButton
				ariaLabel={prevAriaLabel}
				onClick={onClickPrevious}
				disabled={prevButtonDisabled}
				direction="prev"
			/>
			<NavigationHeader
				activeStartDate={activeStartDate}
				ariaLabel={
					typeof navigationAriaLabel === 'string'
						? navigationAriaLabel
						: (navigationAriaLabel?.(activeStartDate, showDoubleView) ?? '')
				}
				ariaLive={navigationAriaLive}
				formatMonthYear={formatMonthYear}
				label={navigationLabel}
				locale={locale}
				nextActiveStartDate={nextActiveStartDate}
				showDoubleView={showDoubleView}
			/>
			<NavigationButton
				ariaLabel={nextAriaLabel}
				onClick={onClickNext}
				disabled={nextButtonDisabled}
				direction="next"
			/>
		</div>
	);
}

type NavigationHeaderProps = {
	activeStartDate: NavigationProps['activeStartDate'];
	ariaLabel?: string;
	ariaLive?: NavigationProps['navigationAriaLive'];
	formatMonthYear?: NavigationProps['formatMonthYear'];
	label?: NavigationProps['navigationLabel'];
	locale: string;
	nextActiveStartDate?: Date;
	showDoubleView?: NavigationProps['showDoubleView'];
};

function NavigationHeader({
	activeStartDate,
	ariaLabel,
	ariaLive,
	formatMonthYear = defaultFormatMonthYear,
	label,
	locale,
	nextActiveStartDate,
	showDoubleView,
}: NavigationHeaderProps) {
	function renderLabel(date: Date) {
		const formatted = (() => {
			return formatMonthYear(locale, date);
		})();

		return label
			? label({
					date,
					label: formatted,
					locale: locale,
				})
			: formatted;
	}

	return (
		<p
			aria-label={ariaLabel}
			aria-live={ariaLive}
			className="m-0 flex flex-row w-full justify-center text-text-primary items-center text-label spacing-inline-l"
		>
			<span className="w-full text-center">
				<BevSafeText>{renderLabel(activeStartDate)}</BevSafeText>
			</span>
			{showDoubleView ? (
				<span className="w-full text-center">
					{renderLabel(nextActiveStartDate ?? getBeginNext(activeStartDate))}
				</span>
			) : null}
		</p>
	);
}

type NavigationButtonProps = {
	ariaLabel?: string;
	label?: React.ReactNode;
	direction?: string;
	buttonRef?: React.RefObject<HTMLButtonElement>;
} & React.ComponentPropsWithRef<'button'>;

function NavigationButton({
	ariaLabel,
	direction,
	label,
	disabled,
	buttonRef,
	...props
}: NavigationButtonProps) {
	if (disabled) {
		props['aria-disabled'] = disabled;
		props.onClick = undefined;
	}

	const isRTL = getRTL();
	return (
		<button
			{...props}
			aria-label={ariaLabel}
			className={cn('text-[1.3rem] cursor-pointer absolute', {
				'right-0': (!isRTL && direction === 'next') || (isRTL && direction === 'prev'),
				'left-0': (!isRTL && direction === 'prev') || (isRTL && direction === 'next'),

				'text-text-primary text-center no-underline z-[2] text-[24px] mt-0': direction,
				invisible: disabled,
			})}
			type="button"
			ref={buttonRef}
		>
			{label ? (
				label
			) : direction ? (
				(!isRTL && (direction === 'prev' || direction === 'prev2')) ||
				(isRTL && (direction === 'next' || direction === 'next2')) ? (
					<IconChevronLeft height={16} width={16} />
				) : (
					<IconChevronRight height={16} width={16} />
				)
			) : null}
		</button>
	);
}
